import * as React from 'react';
import {
	AssetGroup,
	fetchAssetGroups,
	Pageable,
	PaginationSettings,
	searchAssetGroups,
	fetchAssetGroupDefinitions,
	getAncestorGroups
} from '@deviceinsight/ng-ui-api-client';

import {AssetGroupInfo, Trans} from '@deviceinsight/ng-ui-scale-lib/api';

import {AssetsPerAssetGroup} from './types';
import FilterTable from '../search/FilterTable';
import {SearchConfig} from '../search/types';
import {assetGroupFields} from '../search/fields';
import AssetGroupHierarchy from './AssetGroupHierarchy';

const getRowDefs = (onSelect: (arg0: AssetGroup) => void) => ({
	getRowClass: () => 'assetgroup-table-row',
	onClick: ({assetGroup}: AssetGroupInfo) => onSelect(assetGroup)
});

const getColumnDefs = (assetsPerAssetGroup: AssetsPerAssetGroup | null | undefined) => [
	{
		name: 'name',
		label: <Trans i18nKey="assetGroup.name">Name</Trans>,
		sortable: true,
		render: ({assetGroup}: AssetGroupInfo) => assetGroup.displayName || assetGroup.name,
		width: '350px'
	},
	{
		name: 'assetGroups',
		label: <Trans i18nKey="assetGroup.path">Path</Trans>,
		render: ({assetGroup, ancestors}: AssetGroupInfo) => (
			<AssetGroupHierarchy assetGroup={assetGroup} ancestors={ancestors} />
		)
	},
	{
		name: 'numberOfAssets',
		label: <Trans i18nKey="assetGroup.numberOfAssets">Number of Assets</Trans>,
		render: ({assetGroup: {id}}: AssetGroupInfo) => (
			<div>{assetsPerAssetGroup && assetsPerAssetGroup[id].length}</div>
		)
	}
];

type Props = {
	assetGroupIds: Array<number>;
	assetsPerAssetGroup: AssetsPerAssetGroup | null | undefined;
	onSelect: (assetGroup: AssetGroup) => void;
};
type State = {
	searchConfig: SearchConfig<AssetGroupInfo>;
};

const getSearchConfig = (assetGroupIds: Array<number>): SearchConfig<AssetGroupInfo> => {
	return {
		fields: assetGroupFields,
		search: async (query, paginationSettings) => {
			const pageableAssetGroups: Pageable<AssetGroup> = await fetchOrSearchAssetGroups(
				`id:(${assetGroupIds.length ? assetGroupIds.join(' OR ') : 'null'}) ${query}`,
				paginationSettings
			);
			const assetGroups = pageableAssetGroups.content;
			const assetGroupDefinitions = await fetchAssetGroupDefinitions();
			const content = await Promise.all(
				assetGroups.map(async (assetGroup) => {
					const ancestors = await getAncestorGroups(assetGroup.id);
					const assetGroupDefinition = assetGroupDefinitions.find(
						(def) => def.id === assetGroup.definitionId
					);
					if (!assetGroupDefinition) {
						throw Error(`could not find asset group definition with ID ${assetGroup.definitionId}`);
					}
					return {
						id: assetGroup.id,
						assetGroup,
						ancestors,
						assetGroupDefinition
					};
				})
			);
			return {...pageableAssetGroups, content};
		}
	};
};

const SORTABLE_ASSET_GROUP_PROPERTIES = ['id', 'definitionId', 'definitionName', 'parentId', 'name', 'displayName'];

function fetchOrSearchAssetGroups(
	query: string,
	paginationSettings: PaginationSettings
): Promise<Pageable<AssetGroup>> {
	if (query.trim() === '' && SORTABLE_ASSET_GROUP_PROPERTIES.includes(paginationSettings.sort.property)) {
		return fetchAssetGroups({shadow: true}, paginationSettings);
	} else {
		return searchAssetGroups(query, paginationSettings);
	}
}

export default class AssetGroupTable extends React.Component<Props, State> {
	searchConfig: SearchConfig<AssetGroupInfo> = getSearchConfig(this.props.assetGroupIds);

	render() {
		const {onSelect, assetsPerAssetGroup} = this.props;
		return (
			<div className="assetgroup-table">
				<FilterTable
					searchContext=""
					searchConfig={this.searchConfig}
					columnDefinitions={getColumnDefs(assetsPerAssetGroup)}
					rowDefinitions={getRowDefs(onSelect)}
					pageSize={10}
					uniqueItemKey={({assetGroup}) => assetGroup.id}
				/>
			</div>
		);
	}
}
